import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Dialog, DialogContent } from "@mui/material";
import {
  Button,
  Iconography,
  IconographySizes,
} from "@hid-galaxy-ui/galaxy-react";
import "./UnsavedChangesModal.scss";

type UnsavedChangesContextValue = {
  isDirty: boolean;
  setDirty: (dirty: boolean) => void;
  registerDiscardHandler: (handler: (() => void) | null) => void;
  requestNavigation: (action: () => void) => void;
};

const UnsavedChangesContext =
  createContext<UnsavedChangesContextValue | null>(null);

export const useUnsavedChanges = (): UnsavedChangesContextValue => {
  const context = useContext(UnsavedChangesContext);
  if (!context) {
    return {
      isDirty: false,
      setDirty: () => undefined,
      registerDiscardHandler: () => undefined,
      requestNavigation: (action: () => void) => action(),
    };
  }
  return context;
};

export const UnsavedChangesProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [isDirty, setDirtyState] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const isDirtyRef = useRef(false);
  const pendingActionRef = useRef<(() => void) | null>(null);
  const discardHandlerRef = useRef<(() => void) | null>(null);

  const setDirty = useCallback((dirty: boolean) => {
    isDirtyRef.current = dirty;
    setDirtyState(dirty);
  }, []);

  const registerDiscardHandler = useCallback(
    (handler: (() => void) | null) => {
      discardHandlerRef.current = handler;
    },
    []
  );

  const requestNavigation = useCallback((action: () => void) => {
    if (!isDirtyRef.current) {
      action();
      return;
    }
    pendingActionRef.current = action;
    setModalOpen(true);
  }, []);

  const handleStay = () => {
    pendingActionRef.current = null;
    setModalOpen(false);
  };

  const handleLeave = () => {
    const action = pendingActionRef.current;
    pendingActionRef.current = null;
    setModalOpen(false);
    // Clear dirty first so subsequent tab changes are not blocked.
    isDirtyRef.current = false;
    setDirtyState(false);
    discardHandlerRef.current?.();
    action?.();
  };

  useEffect(() => {
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!isDirtyRef.current) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, []);

  const value = useMemo(
    () => ({
      isDirty,
      setDirty,
      registerDiscardHandler,
      requestNavigation,
    }),
    [isDirty, setDirty, registerDiscardHandler, requestNavigation]
  );

  return (
    <UnsavedChangesContext.Provider value={value}>
      {children}
      <Dialog open={modalOpen} onClose={handleStay} maxWidth="sm" fullWidth>
        <div className="bl-unsaved-modal">
          <header className="bl-unsaved-modal-header">
            <div className="bl-unsaved-heading">
              <span className="bl-unsaved-icon" aria-hidden="true">
                <Iconography
                  icon="circleExclamation"
                  size={IconographySizes.Small}
                />
              </span>
              <h3>Unsaved changes</h3>
            </div>
          </header>
          <DialogContent className="bl-unsaved-modal-content">
            <p>
              You have made changes that have not been saved. If you leave this
              page without saving, your changes will be lost.
            </p>
            <p>Are you sure you want to continue?</p>
          </DialogContent>
          <footer className="bl-unsaved-modal-footer">
            <Button variant="secondary" label="CANCEL" onClick={handleStay} />
            <Button variant="primary" label="CONTINUE" onClick={handleLeave} />
          </footer>
        </div>
      </Dialog>
    </UnsavedChangesContext.Provider>
  );
};
